import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { UserGroupDTO } from '../../../../dto/user-group/user-group.dto';
import {
  filterUserGroupsForLocationDisplay,
  isGroupsHideEmptyAtLocationEnabled,
  resolveOrgIdFromUser,
} from '../../../../components/duna/duna-user-groups-location/user-groups-location-display.util';

const LOCATION_GROUPS_LIST_PAGE_SIZE = 10000;

const EVENT_PREFIX = 'duna-user-groups-location';

function resolveLocationGuid(router: Router): string | null {
  const seg = router.url.split('/');
  const idx = seg.indexOf('lokation');
  if (idx === -1 || idx === seg.length - 1) {
    return null;
  }
  return seg[idx + 1];
}

export function duna_user_groups_location(componentDataSourceService: ComponentDataSourceService) {
  const router = componentDataSourceService.injector.get(Router);

  const maintenanceTransform = (value: number, row?: UserGroupDTO) => {
    if (value === undefined || value === null) return 'N/A';
    const intended = Math.min(100, Math.max(0, value));
    const participated = Math.min(100, Math.max(0, row?.progress_percentage || 0));
    return `${intended}% (${participated}%)`;
  };

  return {
    name: 'DUNA Brugergrupperinger (lokation)',
    description: 'Brugergrupperinger med statistik for lokationens brugere',
    execute: (params?: LoadDataParams): Observable<any> => {
      const guid = resolveLocationGuid(router);
      if (!guid) {
        console.error('[duna_user_groups_location] No location GUID in URL');
        return of({ items: [], totalCount: 0, pageSize: 10, pageIndex: 0 });
      }

      const listParams = params ?? ({} as LoadDataParams);
      const mergedFilters = [
        ...(listParams.filters ?? []),
        {
          column: 'pageSize',
          value: LOCATION_GROUPS_LIST_PAGE_SIZE,
          operator: 'eq',
        },
        { column: 'pageIndex', value: 0, operator: 'eq' },
      ];
      const fetchParams = { ...listParams, filters: mergedFilters };

      return componentDataSourceService['loadDataWithStandardFiltersGET'](
        'duna_user_groups_location',
        `/user-groups/by-location/${guid}`,
        fetchParams
      ).pipe(
        catchError(error => {
          console.error('[duna_user_groups_location] Error fetching groups:', error);
          return of({ items: [], totalCount: 0, pageSize: 10, pageIndex: 0 });
        }),
        switchMap((result) => {
          const items = (result?.items || []) as UserGroupDTO[];
          const orgId = resolveOrgIdFromUser(
            componentDataSourceService.authService.getCurrentUser() as Record<
              string,
              unknown
            > | null
          );
          if (orgId == null) {
            const filtered = filterUserGroupsForLocationDisplay(items, true);
            return of({
              items: filtered,
              totalCount: filtered.length,
              pageSize: result?.pageSize ?? 10,
              pageIndex: result?.pageIndex ?? 0,
            });
          }
          const settingsObs = componentDataSourceService.executeDataSource(
            'org_settings',
            { organizationId: orgId }
          );
          if (!settingsObs) {
            const filtered = filterUserGroupsForLocationDisplay(items, true);
            return of({
              items: filtered,
              totalCount: filtered.length,
              pageSize: result?.pageSize ?? 10,
              pageIndex: result?.pageIndex ?? 0,
            });
          }
          return settingsObs.pipe(
            map((settingsRes: { data?: Record<string, unknown> }) => {
              const hideEmpty = isGroupsHideEmptyAtLocationEnabled(
                settingsRes?.data
              );
              const filtered = filterUserGroupsForLocationDisplay(
                items,
                hideEmpty
              );
              return {
                items: filtered,
                totalCount: filtered.length,
                pageSize: result?.pageSize ?? 10,
                pageIndex: result?.pageIndex ?? 0,
              };
            }),
            catchError(() => {
              const filtered = filterUserGroupsForLocationDisplay(items, true);
              return of({
                items: filtered,
                totalCount: filtered.length,
                pageSize: result?.pageSize ?? 10,
                pageIndex: result?.pageIndex ?? 0,
              });
            })
          );
        })
      );
    },
    settings: {
      columns: [
        { key: 'name', header: 'Navn', type: 'text', sortable: true, wrap: true },
        { key: 'description', header: 'Beskrivelse', type: 'text', sortable: true, wrap: true },
        { key: 'member_count', header: 'Antal brugere', type: 'number', sortable: true, alignment: 'center' },
        {
          key: 'ok_percentage',
          header: 'STATUS (%)',
          type: 'text',
          transform: (value: number) => `${value ?? 0}%`,
          sortable: true,
          alignment: 'left',
        },
        {
          key: 'registered_percentage',
          header: 'Tilmeldte',
          type: 'text',
          transform: maintenanceTransform,
          sortable: true,
          alignment: 'left',
        },
        {
          key: 'view_members',
          header: '',
          type: 'button',
          buttonText: 'Se brugere',
          buttonColor: 'primary',
          variant: 'stroked',
          alignment: 'right',
          action: (row: UserGroupDTO) => {
            window.dispatchEvent(new CustomEvent(`${EVENT_PREFIX}-view-members`, { detail: row }));
          },
        },
        {
          key: 'manage_members',
          header: '',
          type: 'button',
          buttonText: 'Tildel brugere',
          buttonColor: 'accent',
          variant: 'raised',
          alignment: 'right',
          action: (row: UserGroupDTO) => {
            window.dispatchEvent(new CustomEvent(`${EVENT_PREFIX}-manage-members`, { detail: row }));
          },
        },
      ],
      pageable: true,
      sortable: true,
      rowHover: true,
      searchable: true,
      defaultSortColumn: 'member_count',
      defaultSortDirection: 'desc',
    },
  };
}

export { resolveLocationGuid };
